import { type TicketSla } from '@gb-ticket/shared-types';
import { TimerIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  TicketSectionCard,
  type TicketSectionTone,
} from '@/products/helpdesk/features/tickets/ticket-section-card';
import { formatCountdown, formatDateTime, formatSlaMinutes } from '@/lib/format';
import { cn } from '@/lib/utils';

function remainingMs(
  dueAt: string | null,
  paused: boolean,
  pausedAt: string | null,
  now: number,
): number | null {
  if (!dueAt) {
    return null;
  }
  const due = new Date(dueAt).getTime();
  const reference = paused && pausedAt ? new Date(pausedAt).getTime() : now;
  return due - reference;
}

function CountdownRow({
  label,
  dueAt,
  remaining,
  breached,
  doneLabel,
}: {
  label: string;
  dueAt: string | null;
  remaining: number | null;
  breached: boolean;
  doneLabel: string;
}) {
  return (
    <div className="space-y-1 rounded-md border bg-background/70 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{label}</p>
        {breached ? (
          <Badge variant="destructive">verletzt</Badge>
        ) : remaining === null ? (
          <Badge variant="secondary">{doneLabel}</Badge>
        ) : remaining < 0 ? (
          <Badge variant="destructive">überfällig</Badge>
        ) : remaining < 30 * 60_000 ? (
          <Badge className="border-amber-300 bg-amber-50 text-amber-900" variant="outline">
            bald fällig
          </Badge>
        ) : null}
      </div>
      <p
        className={cn(
          'font-mono text-lg text-primary tabular-nums',
          remaining !== null && remaining < 0 && 'text-destructive',
        )}
      >
        {remaining === null ? '—' : formatCountdown(remaining)}
      </p>
      <p className="text-xs text-muted-foreground">Frist: {formatDateTime(dueAt)}</p>
    </div>
  );
}

export function SlaCountdown({ sla }: { sla: TicketSla | null }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!sla || sla.paused) {
      return;
    }
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [sla]);

  if (!sla) {
    return (
      <TicketSectionCard
        tone="muted"
        icon={TimerIcon}
        title="SLA"
        description="Für dieses Ticket ist noch keine Policy zugeordnet."
      />
    );
  }

  const first = remainingMs(sla.firstResponseDueAt, sla.paused, sla.pausedAt, now);
  const resolution = remainingMs(sla.resolutionDueAt, sla.paused, sla.pausedAt, now);
  const breached = sla.firstResponseBreached || sla.resolutionBreached;
  const urgent =
    !sla.paused &&
    ((first !== null && first < 30 * 60_000) || (resolution !== null && resolution < 30 * 60_000));

  let tone: TicketSectionTone = 'steel';
  if (breached) tone = 'red';
  else if (sla.paused || urgent) tone = 'amber';

  return (
    <TicketSectionCard
      tone={tone}
      icon={TimerIcon}
      title="SLA"
      description={`${sla.policyName} · Reaktion ${formatSlaMinutes(sla.firstResponseMinutes)} · Lösung ${formatSlaMinutes(sla.resolutionMinutes)}`}
    >
      <div className="space-y-3">
        {sla.paused && (
          <Badge className="border-amber-300 bg-amber-50 text-amber-900" variant="outline">
            Timer pausiert
            {sla.pausedAt ? ` seit ${formatDateTime(sla.pausedAt)}` : ''}
          </Badge>
        )}
        <CountdownRow
          label="Erste Reaktion"
          dueAt={sla.firstResponseDueAt}
          remaining={first}
          breached={sla.firstResponseBreached}
          doneLabel="erfüllt"
        />
        <CountdownRow
          label="Lösung"
          dueAt={sla.resolutionDueAt}
          remaining={resolution}
          breached={sla.resolutionBreached}
          doneLabel="keine offene Frist"
        />
      </div>
    </TicketSectionCard>
  );
}
